import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getSupabaseForMode } from '@/api/supabaseClient.ts';
import type { SupabaseClient } from '@supabase/supabase-js';
import styles from './ConnectionStatus.module.css';

type Status = 'connected' | 'connecting' | 'disconnected';

export function ConnectionStatus({ sessionId }: { sessionId: string }) {
  const { t } = useTranslation('common');
  const [status, setStatus] = useState<Status>('connecting');

  useEffect(() => {
    if (!sessionId) {
      setStatus('disconnected');
      return;
    }

    let interval: ReturnType<typeof setInterval>;
    let timeout: ReturnType<typeof setTimeout>;
    let cancelled = false;

    function pollChannels(sb: SupabaseClient) {
      const channels = sb.getChannels();
      // Find any channel for this session (may be multiple briefly during StrictMode)
      const reviewChannels = channels.filter(ch => ch.topic === `realtime:review-${sessionId}`);

      if (reviewChannels.length === 0) {
        setStatus('connecting');
      } else if (reviewChannels.some(ch => ch.state === 'joined')) {
        setStatus('connected');
      } else if (reviewChannels.some(ch => ch.state === 'joining')) {
        setStatus('connecting');
      } else {
        setStatus('disconnected');
      }
    }

    getSupabaseForMode().then(sb => {
      if (cancelled || !sb) {
        setStatus('disconnected');
        return;
      }
      interval = setInterval(() => pollChannels(sb), 2000);
      timeout = setTimeout(() => pollChannels(sb), 3000);
    });

    return () => {
      cancelled = true;
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [sessionId]);

  const labelKeys: Record<Status, string> = {
    connected: 'connectionLive',
    connecting: 'connectionConnecting',
    disconnected: 'connectionOffline',
  };

  const label = t(labelKeys[status]);

  return (
    <span className={`${styles.status} ${styles[status]}`} title={t('realtimeSyncStatus', { status: label })}>
      <span className={styles.dot} />
      {label}
    </span>
  );
}
